import React, { useEffect, useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { images } from '../../shared/images/images';
import logoNuevo from '../../shared/images/logoEmcali.jpg';
import { getProyectosApiBaseUrl } from '../../utils/proyectosApiBaseUrl';

const schema = Yup.object({
  contraseña: Yup.string().min(6, 'Minimo 6 caracteres').required('La contraseña es obligatoria'),
  confirmar: Yup.string()
    .oneOf([Yup.ref('contraseña')], 'Las contraseñas no coinciden')
    .required('Confirme la contraseña'),
});

const theme = createTheme({
  palette: { primary: { main: '#0a2f57' } },
  shape: { borderRadius: 14 },
});

export default function RestablecerContrasenaPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = String(searchParams.get('token') || '').trim();

  const [validando, setValidando] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [usuarioHint, setUsuarioHint] = useState('');
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidando(false);
      setTokenError('No se recibió un enlace válido. Solicite uno nuevo desde «Olvidé mi contraseña».');
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${getProyectosApiBaseUrl()}/auth/validar-token-recuperacion?token=${encodeURIComponent(token)}`,
        );
        const data = await res.json();
        if (data.valido) {
          setTokenValido(true);
          setUsuarioHint(data.usuario ? `Usuario: ${data.usuario}` : '');
        } else {
          setTokenError(data.mensaje || 'Enlace no válido o expirado.');
        }
      } catch {
        setTokenError('No fue posible validar el enlace.');
      } finally {
        setValidando(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (
    values: { contraseña: string; confirmar: string },
    { setSubmitting }: { setSubmitting: (v: boolean) => void },
  ) => {
    try {
      setError(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/auth/restablecer-clave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, contraseña: values.contraseña }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'No fue posible actualizar la contraseña.');
      setOk(data.mensaje || 'Contraseña actualizada.');
      setTimeout(() => navigate('/'), 2500);
    } catch (e: any) {
      setError(e.message || 'Error al restablecer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        component="main"
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(10,47,87,.50), rgba(10,47,87,.70)), url(${images.SliderLogin})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <CssBaseline />
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Paper elevation={8} sx={{ width: '100%', maxWidth: 460, p: 4, borderRadius: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" sx={{ width: 160, objectFit: 'contain' }} alt="Logo Emcali" src={logoNuevo} />
              <Typography variant="h5" fontWeight={700} color="primary.main">
                Nueva contraseña
              </Typography>
              {usuarioHint && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {usuarioHint}
                </Typography>
              )}
            </Box>

            {validando && (
              <Typography align="center" color="text.secondary">
                Validando enlace...
              </Typography>
            )}

            {!validando && tokenError && (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {tokenError}
                </Alert>
                <Box textAlign="center">
                  <Link component={RouterLink} to="/olvido-contrasena">
                    Solicitar nuevo enlace
                  </Link>
                </Box>
              </>
            )}

            {!validando && tokenValido && (
              <>
                {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {!ok && (
                  <Formik
                    initialValues={{ contraseña: '', confirmar: '' }}
                    validationSchema={schema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting, touched, errors }) => (
                      <Form>
                        <Field
                          as={TextField}
                          name="contraseña"
                          label="Nueva contraseña"
                          type={showPass ? 'text' : 'password'}
                          fullWidth
                          margin="normal"
                          error={Boolean(touched.contraseña && errors.contraseña)}
                          helperText={<ErrorMessage name="contraseña" />}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockOutlinedIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowPass((s) => !s)} edge="end">
                                  {showPass ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Field
                          as={TextField}
                          name="confirmar"
                          label="Confirmar contraseña"
                          type={showConfirm ? 'text' : 'password'}
                          fullWidth
                          margin="normal"
                          error={Boolean(touched.confirmar && errors.confirmar)}
                          helperText={<ErrorMessage name="confirmar" />}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end">
                                  {showConfirm ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          disabled={isSubmitting}
                          sx={{ mt: 2, py: 1.2, fontWeight: 700, textTransform: 'none' }}
                        >
                          {isSubmitting ? 'Guardando...' : 'Guardar nueva contraseña'}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                )}
              </>
            )}

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/" underline="hover">
                Volver al inicio de sesión
              </Link>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
